<!--
  OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
  all the essential functionalities required for any enterprise.
  Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com

  OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
  the GNU General Public License as published by the Free Software Foundation, either
  version 3 of the License, or (at your option) any later version.

  OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU General Public License for more details.

  You should have received a copy of the GNU General Public License along with OrangeHRM.
  If not, see <https://www.gnu.org/licenses/>.
-->
<template>
  <div class="cal-sheet-backdrop" @click.self="$emit('close')">
    <aside class="cal-side-sheet" @click.stop>
      <!-- Header -->
      <div class="orangehrm-modal-header cal-sheet-header">
        <oxd-text type="card-title">
          {{
            isAdmin ? (isEdit ? 'Edit Event' : 'New Event') : 'Event Details'
          }}
        </oxd-text>
        <button
          type="button"
          class="cal-sheet-close"
          title="Close"
          aria-label="Close panel"
          @click="$emit('close')"
        >
          &times;
        </button>
      </div>
      <oxd-divider />

      <!-- ESS: read-only view -->
      <div v-if="!isAdmin" class="cal-sheet-body">
        <oxd-form-row>
          <oxd-text type="span" class="orangehrm-sub-title">Title</oxd-text>
          <oxd-text type="p">{{ event.title }}</oxd-text>
        </oxd-form-row>
        <oxd-form-row v-if="event.description">
          <oxd-text type="span" class="orangehrm-sub-title">Description</oxd-text>
          <oxd-text type="p">{{ event.description }}</oxd-text>
        </oxd-form-row>
        <oxd-form-row v-if="event.location">
          <oxd-text type="span" class="orangehrm-sub-title">Location</oxd-text>
          <oxd-text type="p">{{ event.location }}</oxd-text>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-text type="span" class="orangehrm-sub-title">Date &amp; Time</oxd-text>
          <oxd-text type="p">{{ formatDisplay(event.eventDatetime) }}</oxd-text>
        </oxd-form-row>
        <oxd-form-row v-if="event.notes">
          <oxd-text type="span" class="orangehrm-sub-title">Notes</oxd-text>
          <oxd-text type="p">{{ event.notes }}</oxd-text>
        </oxd-form-row>
      </div>

      <!-- Admin: editable form -->
      <div v-else class="cal-sheet-body">
        <oxd-form @submit.prevent="saveEvent">
          <oxd-form-row>
            <oxd-input-field
              v-model="form.title"
              label="Title"
              :required="true"
              maxlength="250"
              placeholder="Event title"
            />
          </oxd-form-row>

          <oxd-form-row>
            <oxd-input-field
              v-model="form.description"
              type="textarea"
              label="Description"
              placeholder="Optional description"
            />
          </oxd-form-row>

          <oxd-form-row>
            <oxd-input-field
              v-model="form.location"
              label="Location"
              placeholder="Optional location"
              maxlength="250"
            />
          </oxd-form-row>

          <oxd-form-row>
            <date-input v-model="form.date" label="Event Date" :required="true" />
          </oxd-form-row>

          <oxd-form-row>
            <time-input v-model="form.time" label="Event Time" />
          </oxd-form-row>

          <oxd-form-row>
            <oxd-input-field
              v-model="form.notes"
              type="textarea"
              label="Notes"
              placeholder="Optional notes"
            />
          </oxd-form-row>

          <oxd-text v-if="errorMsg" class="cal-sheet-error" type="p">
            {{ errorMsg }}
          </oxd-text>
        </oxd-form>
      </div>

      <oxd-divider />

      <!-- Admin footer -->
      <div v-if="isAdmin" class="orangehrm-modal-footer">
        <oxd-button
          display-type="ghost"
          label="Cancel"
          @click="$emit('close')"
        />
        <oxd-button
          v-if="isEdit"
          display-type="label-danger"
          label="Delete"
          icon-name="trash"
          class="orangehrm-left-space"
          :disabled="isSaving"
          @click="deleteEvent"
        />
        <oxd-button
          display-type="secondary"
          :label="isSaving ? 'Saving…' : 'Save'"
          class="orangehrm-left-space"
          :disabled="isSaving"
          @click="saveEvent"
        />
      </div>
      <!-- Read-only footer -->
      <div v-else class="orangehrm-modal-footer">
        <oxd-button
          display-type="ghost"
          label="Close"
          @click="$emit('close')"
        />
      </div>
    </aside>

    <delete-confirmation ref="deleteDialog" />
  </div>
</template>

<script>
import {ref, computed, reactive, watch} from 'vue';
import {
  OxdButton,
  OxdText,
  OxdDivider,
  OxdForm,
  OxdFormRow,
  OxdInputField,
} from '@ohrm/oxd';
import {APIService} from '@/core/util/services/api.service';
import {formatDateKey, isNonWorkingDay} from '../utils/calendarUtils';
import DateInput from '@/core/components/inputs/DateInput.vue';
import TimeInput from '@/core/components/inputs/TimeInput.vue';
import DeleteConfirmationDialog from '@/core/components/dialogs/DeleteConfirmationDialog.vue';

export default {
  name: 'EventSideSheet',

  components: {
    DateInput,
    TimeInput,
    'oxd-button': OxdButton,
    'oxd-text': OxdText,
    'oxd-divider': OxdDivider,
    'oxd-form': OxdForm,
    'oxd-form-row': OxdFormRow,
    'oxd-input-field': OxdInputField,
    'delete-confirmation': DeleteConfirmationDialog,
  },

  props: {
    event: {type: Object, default: null}, // null = create mode
    preDate: {type: Date, default: null}, // pre-populated date when creating
    workweek: {type: Object, default: null},
    holidaysByDay: {type: Map, default: () => new Map()},
    isAdmin: {type: Boolean, default: false},
  },

  emits: ['close', 'saved', 'deleted'],

  setup(props, {emit}) {
    const isEdit = computed(() => Boolean(props.event?.id));
    const isSaving = ref(false);
    const errorMsg = ref('');

    const form = reactive({
      title: '',
      description: '',
      location: '',
      date: '',
      time: '09:00',
      notes: '',
    });

    // Populate form whenever event/preDate changes
    watch(
      () => [props.event, props.preDate],
      () => {
        errorMsg.value = '';
        if (props.event) {
          form.title = props.event.title ?? '';
          form.description = props.event.description ?? '';
          form.location = props.event.location ?? '';
          const dt = props.event.eventDatetime ?? '';
          form.date = dt.slice(0, 10);
          form.time = dt.length >= 16 ? dt.slice(11, 16) : '09:00';
          form.notes = props.event.notes ?? '';
        } else {
          form.title = '';
          form.description = '';
          form.location = '';
          const d =
            props.preDate instanceof Date
              ? props.preDate
              : props.preDate
              ? new Date(props.preDate)
              : new Date();
          form.date = formatDateKey(d);
          form.time = '09:00';
          form.notes = '';
        }
      },
      {immediate: true},
    );

    const eventsHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/calendar/events',
    );

    async function saveEvent() {
      if (!form.title.trim()) {
        errorMsg.value = 'Title is required.';
        return;
      }
      if (!form.date) {
        errorMsg.value = 'Date is required.';
        return;
      }

      // Check holidays and non-working days
      const dateParts = form.date.split('-');
      if (dateParts.length === 3) {
        const selectedDate = new Date(
          Number(dateParts[0]),
          Number(dateParts[1]) - 1,
          Number(dateParts[2]),
        );

        const hasHoliday =
          (props.holidaysByDay?.get(form.date) || []).length > 0;
        if (hasHoliday) {
          errorMsg.value = 'Events cannot be scheduled on a company holiday.';
          return;
        }

        if (isNonWorkingDay(selectedDate, props.workweek)) {
          errorMsg.value = 'Events cannot be scheduled on a non-working day.';
          return;
        }
      }

      isSaving.value = true;
      errorMsg.value = '';

      const timeVal = form.time ? form.time.slice(0, 5) : '00:00';
      const eventDatetime = `${form.date} ${timeVal}:00`;

      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        location: form.location.trim() || null,
        eventDatetime,
        notes: form.notes.trim() || null,
      };

      try {
        if (isEdit.value) {
          await eventsHttp.update(props.event.id, payload);
        } else {
          await eventsHttp.create(payload);
        }
        emit('saved');
      } catch (err) {
        const msg =
          err?.response?.data?.error?.message ||
          'Failed to save event. Please try again.';
        errorMsg.value = msg;
      } finally {
        isSaving.value = false;
      }
    }

    const deleteDialog = ref(null);

    async function deleteEvent() {
      const confirmation = await deleteDialog.value.showDialog();
      if (confirmation !== 'ok') return;

      isSaving.value = true;
      errorMsg.value = '';
      try {
        await eventsHttp.deleteAll({ids: [props.event.id]});
        emit('deleted');
      } catch {
        errorMsg.value = 'Failed to delete event.';
      } finally {
        isSaving.value = false;
      }
    }

    function formatDisplay(datetimeStr) {
      if (!datetimeStr) return '';
      const d = new Date(datetimeStr.replace(' ', 'T'));
      return d.toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }

    return {
      isEdit,
      isSaving,
      errorMsg,
      form,
      saveEvent,
      deleteEvent,
      formatDisplay,
      deleteDialog,
    };
  },
};
</script>

<style lang="scss" scoped>
::v-deep(.oxd-divider) {
  width: 100% !important;
  border: 0 !important;
  border-top: 1px solid var(--oxd-interface-gray-lighten-2-color, #cbd5e1) !important;
  margin: 0 !important;
}

.orangehrm-modal-footer {
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end !important;
  padding: 1.25rem 1.5rem !important;
  gap: 0.5rem;
  margin-top: 0 !important;
  border-top: 0 !important;

  .orangehrm-left-space {
    margin-left: 0;
  }
}
</style>
